import { Task } from '../../types/workOrders';
import { CheckCircle2, Circle } from 'lucide-react';
import { useLanguageStore } from '../../store/languageStore';
import translations from '../../translations';

interface TaskSummaryProps {
  tasks: Task[];
}

export function TaskSummary({ tasks }: TaskSummaryProps) {
  const { language } = useLanguageStore();
  const t = translations[language];
  const completedTasks = tasks.filter(task => task.isCompleted);
  const pendingTasks = tasks.filter(task => !task.isCompleted);
  const completionRate = tasks.length > 0 
    ? Math.round((completedTasks.length / tasks.length) * 100) 
    : 0;

  return (
    <div className="bg-light-card dark:bg-dark-card rounded-lg p-6 border border-light-border dark:border-dark-border shadow-soft dark:shadow-soft-dark">
      <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">{t.dashboard.taskSummary.title}</h2>
      
      {/* Progress Bar */}
      <div className="mb-6">
        <div className="flex justify-between items-center mb-2">
          <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
            {t.dashboard.taskSummary.completionRate}
          </span>
          <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
            {completionRate}%
          </span>
        </div>
        <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2.5">
          <div 
            className="bg-primary-600 h-2.5 rounded-full transition-all duration-500"
            style={{ width: `${completionRate}%` }}
          />
        </div>
      </div>

      {/* Task Lists */}
      <div className="space-y-4">
        {/* Completed Tasks */}
        <div>
          <h3 className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-2">
            {t.dashboard.taskSummary.recentlyCompleted}
          </h3>
          <div className="space-y-2">
            {completedTasks.slice(0, 3).map(task => (
              <div 
                key={task.id}
                className="flex items-start gap-2 p-2 rounded-lg bg-green-50 dark:bg-green-900/20"
              >
                <CheckCircle2 className="h-5 w-5 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" />
                <span className="text-sm text-gray-900 dark:text-gray-100">{task.desc}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Pending Tasks */}
        <div>
          <h3 className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-2">
            {t.dashboard.taskSummary.pendingTasks}
          </h3>
          <div className="space-y-2">
            {pendingTasks.slice(0, 3).map(task => (
              <div 
                key={task.id}
                className="flex items-start gap-2 p-2 rounded-lg bg-gray-50 dark:bg-gray-900/20"
              >
                <Circle className="h-5 w-5 text-gray-400 dark:text-gray-500 flex-shrink-0 mt-0.5" />
                <span className="text-sm text-gray-900 dark:text-gray-100">{task.desc}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}